import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, PutCommand, ScanCommand, DeleteCommand, GetCommand } from '@aws-sdk/lib-dynamodb';
import { getAWSConfig } from '../config/aws';

const TABLE_NAME = process.env.REACT_APP_DYNAMODB_TABLE || 'rds-restored-instances';

let cachedCredentials = null;
let docClient = null;

const getDocClient = (awsCredentials = null) => {
  // Create new client if credentials changed or don't exist
  if (!docClient || (awsCredentials && awsCredentials !== cachedCredentials)) {
    cachedCredentials = awsCredentials;
    const client = new DynamoDBClient(getAWSConfig(awsCredentials));
    docClient = DynamoDBDocumentClient.from(client);
  }
  return docClient;
};

export const saveRestoredInstance = async (instanceData, awsCredentials = null) => {
  try {
    const client = getDocClient(awsCredentials);
    const command = new PutCommand({
      TableName: TABLE_NAME,
      Item: {
        instanceId: instanceData.DBInstanceIdentifier,
        snapshotId: instanceData.snapshotId,
        status: instanceData.DBInstanceStatus || 'creating',
        endpoint: instanceData.Endpoint?.Address || null,
        port: instanceData.Endpoint?.Port || null,
        engine: instanceData.Engine,
        instanceClass: instanceData.DBInstanceClass,
        createdAt: new Date().toISOString(),
        ...instanceData
      }
    });

    await client.send(command);
    return true;
  } catch (error) {
    console.error('Error saving instance to DynamoDB:', error);
    throw error;
  }
};

export const getRestoredInstances = async (awsCredentials = null) => {
  try {
    const client = getDocClient(awsCredentials);
    const command = new ScanCommand({
      TableName: TABLE_NAME,
      FilterExpression: 'attribute_exists(snapshotId) AND NOT begins_with(instanceId, :prefix)',
      ExpressionAttributeValues: {
        ':prefix': 'ip-history-'
      }
    });

    const response = await client.send(command);
    return response.Items || [];
  } catch (error) {
    console.error('Error getting instances from DynamoDB:', error);
    throw error;
  }
};

export const deleteRestoredInstance = async (instanceId, awsCredentials = null) => {
  try {
    const client = getDocClient(awsCredentials);
    const command = new DeleteCommand({
      TableName: TABLE_NAME,
      Key: {
        instanceId: instanceId
      }
    });

    await client.send(command);
    return true;
  } catch (error) {
    console.error('Error deleting instance from DynamoDB:', error);
    throw error;
  }
};

export const updateInstanceStatus = async (instanceId, instanceData, awsCredentials = null) => {
  try {
    const client = getDocClient(awsCredentials);
    // Get existing record first
    const getCommand = new GetCommand({
      TableName: TABLE_NAME,
      Key: {
        instanceId: instanceId
      }
    });

    const existingRecord = await client.send(getCommand);

    if (existingRecord.Item) {
      // Update existing record with new data
      const updatedItem = {
        ...existingRecord.Item,
        status: instanceData.DBInstanceStatus,
        endpoint: instanceData.Endpoint?.Address || existingRecord.Item.endpoint,
        port: instanceData.Endpoint?.Port || existingRecord.Item.port,
        engine: instanceData.Engine || existingRecord.Item.engine,
        instanceClass: instanceData.DBInstanceClass || existingRecord.Item.instanceClass,
        updatedAt: new Date().toISOString()
      };

      const putCommand = new PutCommand({
        TableName: TABLE_NAME,
        Item: updatedItem
      });

      await client.send(putCommand);
      console.log(`Updated instance ${instanceId} with status: ${instanceData.DBInstanceStatus}, endpoint: ${instanceData.Endpoint?.Address || 'N/A'}`);
      return true;
    } else {
      console.log(`Instance ${instanceId} not found in DynamoDB`);
      return false;
    }
  } catch (error) {
    console.error('Error updating instance status:', error);
    throw error;
  }
};

const IP_HISTORY_TABLE = process.env.REACT_APP_DYNAMODB_TABLE || 'rds-restored-instances';

export const getUserIpHistory = async (userEmail, awsCredentials = null) => {
  try {
    const client = getDocClient(awsCredentials);
    const command = new GetCommand({
      TableName: IP_HISTORY_TABLE,
      Key: {
        instanceId: `ip-history-${userEmail}`
      }
    });

    const response = await client.send(command);
    return response.Item?.ipAddresses || [];
  } catch (error) {
    console.error('Error getting user IP history:', error);
    return [];
  }
};

export const saveUserIpHistory = async (userEmail, ipAddresses, awsCredentials = null) => {
  try {
    const client = getDocClient(awsCredentials);
    const command = new PutCommand({
      TableName: IP_HISTORY_TABLE,
      Item: {
        instanceId: `ip-history-${userEmail}`,
        ipAddresses: ipAddresses,
        updatedAt: new Date().toISOString()
      }
    });

    await client.send(command);
    return true;
  } catch (error) {
    console.error('Error saving user IP history:', error);
    throw error;
  }
};